/**
 * The editor bar's readiness: the rung the server holds the programme on, or
 * the failed read and its retry.
 *
 * @module components/editor/EditorReadiness
 */

import React from "react";

import { ReadinessBadge } from "@/components/shared/ReadinessBadge";
import { Button, countLabel, Text } from "@/components/ui";
import { blockersForGate, READINESS_RANK, READINESS_RUNGS } from "@/server/readiness-ladder";
import type { ReadinessResult } from "@/server/types/readiness";

/**
 * The rung the server holds the programme on, and how many blockers stand
 * before the next one, named so it is not read as the governance stage's count.
 */
function ReadinessSummary({ readiness }: { readiness: ReadinessResult }): React.ReactElement {
  const next = READINESS_RUNGS[READINESS_RANK[readiness.state] + 1];
  const count = next ? blockersForGate(readiness.blockers, next).length : 0;
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="readiness-badge">
      {/* Named for a screen reader: the sidebar shows the workflow state in
          the same chip style, and the chip alone does not say which it is. */}
      <span className="sr-only">Readiness:</span>
      <ReadinessBadge state={readiness.state} size="sm" />
      {count > 0 ? (
        <Text as="span" tone="muted">
          <span aria-hidden>· </span>
          {countLabel(count, "blocker")} to {next}
        </Text>
      ) : null}
    </span>
  );
}

export interface EditorReadinessProps {
  /** The server's readiness for the programme, once it has been read. */
  readiness: ReadinessResult | null;
  /** The last readiness read failed, so the rung may be out of date. */
  failed: boolean;
  /** Reads the readiness again. */
  onRetry?: () => void;
  /** A readiness read asked for by hand is out. */
  retrying: boolean;
}

/** The rung, or why it cannot be shown and a way to read it again. */
export function EditorReadiness({
  readiness,
  failed,
  onRetry = undefined,
  retrying,
}: EditorReadinessProps): React.ReactElement {
  return (
    <>
      {failed || retrying ? (
        <Text as="span" className="inline-flex items-center gap-2" data-testid="readiness-failed">
          {retrying ? "Checking readiness…" : "Readiness could not be checked."}
          <Button variant="ghost" size="sm" loading={retrying} onClick={onRetry}>
            Try again
          </Button>
        </Text>
      ) : null}
      {readiness && !failed && !retrying ? <ReadinessSummary readiness={readiness} /> : null}
    </>
  );
}
